import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Modal, Button } from 'react-bootstrap';
import { API_URL } from '../../constants';
import { FormGenerator } from '../../helper/FormGenerator';
import { useTokens } from '../../context/TokenContext';
import { countryOptions, stateOptions } from '../../utils/appUtils';

const EditPickupLocation = ({ vendorData, onReload }) => {
    const { getItem } = useTokens();
    const authToken = getItem('vendor', 'AuthToken');
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        address2: '',
        city: '',
        pinCode: '',
        state: '',
        country: 'India',
        addressType: 'Warehouse'
    });
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [shipLoading, setShipLoading] = useState(false);
    const [isApproved, setIsApproved] = useState(false);
    const [userAddresses, setUserAddresses] = useState([]);
    const [showAddressModal, setShowAddressModal] = useState(false);

    const isRestricted = vendorData && vendorData.status === '1' && vendorData.roles === 'ROLE_VENDOR';

    useEffect(() => {
        if (vendorData) {
            // Use warehouses from vendorData instead of fetching separately
            const vendorWarehouses = vendorData.warehouses || [];
            setWarehouses(vendorWarehouses);

            // If there are warehouses, select the first one (considered primary)
            if (vendorWarehouses.length > 0) {
                const firstWarehouse = vendorWarehouses[0];
                setSelectedWarehouse(firstWarehouse);
                setIsApproved(firstWarehouse.approvalStatus === 'approved');
                setFormData({
                    name: firstWarehouse.name || '',
                    email: firstWarehouse.email || '',
                    phone: firstWarehouse.phone || '',
                    address: firstWarehouse.address || '',
                    address2: firstWarehouse.address2 || '',
                    city: firstWarehouse.city || '',
                    pinCode: firstWarehouse.pinCode || '',
                    state: firstWarehouse.state || '',
                    country: firstWarehouse.country || 'India',
                    addressType: firstWarehouse.addressType || 'Warehouse'
                });
            }
        }
        fetchUserAddresses();
    }, [vendorData]);

    const fetchUserAddresses = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/addresses`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
            });

            if (response.status === 200) {
                setUserAddresses(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch user addresses:', error);
        }
    };



    const handleWarehouseSelect = (warehouse) => {
        setSelectedWarehouse(warehouse);
        setIsApproved(warehouse.approvalStatus === 'approved');
        setFormData({
            name: warehouse.name || '',
            email: warehouse.email || '',
            phone: warehouse.phone || '',
            address: warehouse.address || '',
            address2: warehouse.address2 || '',
            city: warehouse.city || '',
            pinCode: warehouse.pinCode || '',
            state: warehouse.state || '',
            country: warehouse.country || 'India',
            addressType: warehouse.addressType || 'Warehouse'
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        setLoading(true);
        const toastId = toast.loading('Saving warehouse details...', { autoClose: false });

        // Validate required fields
        if (!formData.name || !formData.phone || !formData.email || !formData.address || !formData.city || !formData.pinCode || !formData.state) {
            toast.update(toastId, {
                render: 'Please fill out all required fields.',
                type: 'error',
                isLoading: false,
                autoClose: 3000,
            });
            setLoading(false);
            return;
        }

        try {
            const warehouseData = {
                wh_name: formData.name,
                wh_email: formData.email,
                wh_phone: formData.phone,
                wh_address: formData.address,
                wh_address_2: formData.address2,
                wh_city: formData.city,
                wh_pin_code: formData.pinCode,
                wh_state: formData.state,
                wh_country: formData.country
            };

            const response = await axios.put(`${API_URL}/vendor/update`, warehouseData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
            });

            if (response.status === 200 || response.status === 201) {
                toast.update(toastId, {
                    render: 'Warehouse details saved successfully!',
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                });
                // Refresh data
                if (onReload) onReload();
            }
        } catch (error) {
            console.error('Failed to save warehouse:', error);
            toast.update(toastId, {
                render: error.response?.data?.message || 'Failed to save warehouse details. Please try again.',
                type: 'error',
                isLoading: false,
                autoClose: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLoadFromAddress = (address) => {
        setFormData({
            name: address.name || '',
            email: '', // Email not available in address
            phone: address.mobileNumber || '',
            address: address.address || '',
            address2: address.locality || '',
            city: address.city || '',
            pinCode: address.pincode || '',
            state: address.state || '',
            country: address.country || 'India',
            addressType: 'Warehouse' // Always set as warehouse
        });
        setShowAddressModal(false);
        toast.success('Address loaded successfully! Please fill in the email field.');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    return (
        loading ? (
            <div className="loading-spinner text-center">
                <span className="spinner-border spinner-border-sm me-3" role="status" aria-hidden="true"></span>
                Loading...
            </div>
        ) : (
            <div>
                <h2>Pickup Address</h2>
                <p>Fill in your pickup point or delivery boy's warehouse details below.</p>

                {/* Status Indicator */}
                <div className="border p-3 my-3">
                    <h5 className="card-title mb-3 d-inline">
                        {selectedWarehouse?.approvalStatus === 'approved' ? (
                            <>
                                <i className="bi bi-check-circle text-success me-2"></i>
                                Approved & Linked with ShipRocket
                            </>
                        ) : selectedWarehouse?.approvalStatus === 'rejected' ? (
                            <>
                                <i className="bi bi-x-circle text-danger me-2"></i>
                                Rejected
                            </>
                        ) : (
                            <>
                                <i className="bi bi-clock text-warning me-2"></i>
                                Pending Approval
                            </>
                        )}
                    </h5>
                    
                    {/* Pickup Location Name */}
                    {selectedWarehouse?.pickupCode && (
                        <div className="mb-2">
                            <strong>Pickup Location Name:</strong> {selectedWarehouse.pickupCode}
                            <small className="text-muted ms-2">(Generated by System)</small>
                        </div>
                    )}
                    
                    {/* Approval Status */}
                    <div className="mb-2">
                        <strong>Approval Status:</strong> 
                        {selectedWarehouse?.approvalStatus === 'approved' ? (
                            <span className="badge bg-success ms-2">Approved</span>
                        ) : selectedWarehouse?.approvalStatus === 'rejected' ? (
                            <span className="badge bg-danger ms-2">Rejected</span>
                        ) : (
                            <span className="badge bg-warning ms-2">Pending</span>
                        )}
                        <small className="text-muted ms-2">Indicates whether the registration has been approved by the shipping partner.</small>
                    </div>
                    
                    {selectedWarehouse?.approvalStatus === 'approved' && selectedWarehouse?.shiprocketPickupId && (
                        <p className="text-muted small mb-0">
                            ShipRocket Pickup ID: {selectedWarehouse.shiprocketPickupId}
                        </p>
                    )}
                    {selectedWarehouse?.approvalStatus === 'approved' && (
                        <div className="alert alert-info mt-2">
                            <small>This warehouse has been approved and linked with ShipRocket. Further edits are not allowed.</small>
                        </div>
                    )}
                </div>

                {/* Bootstrap Form */}
                <form onSubmit={handleFormSubmit}>
                    <div className='row'>
                        <FormGenerator
                            fields={[
                                {
                                    id: 'name',
                                    label: 'Contact Person Name',
                                    type: 'text',
                                    class: 'col-md-12',
                                    required: true,
                                    readonly: isApproved || isRestricted
                                },
                                {
                                    id: 'email',
                                    label: 'Contact Person Email',
                                    type: 'email',
                                    class: 'col-md-6',
                                    required: true,
                                    readonly: isApproved || isRestricted
                                },
                                {
                                    id: 'phone',
                                    label: 'Contact Person Phone',
                                    type: 'tel',
                                    class: 'col-md-6',
                                    required: true,
                                    maxLength: 10,
                                    minLength: 10,
                                    prefix: '+91',
                                    isNumeric: true,
                                    readonly: isApproved || isRestricted
                                },
                                {
                                    id: 'address',
                                    label: 'Address Line 1',
                                    type: 'textarea',
                                    class: 'col-md-6',
                                    required: true,
                                    readonly: isApproved || isRestricted
                                },
                                {
                                    id: 'address2',
                                    label: 'Address Line 2',
                                    type: 'textarea',
                                    class: 'col-md-6',
                                    required: false,
                                    readonly: isApproved || isRestricted
                                },
                                {
                                    id: 'city',
                                    label: 'City',
                                    type: 'text',
                                    class: 'col-md-6',
                                    required: true,
                                    readonly: isApproved || isRestricted
                                },
                                {
                                    id: 'pinCode',
                                    label: 'Pincode',
                                    type: 'text',
                                    class: 'col-md-6',
                                    required: true,
                                    maxLength: 6,
                                    minLength: 6,
                                    isNumeric: true,
                                    readonly: isApproved || isRestricted
                                },
                                {
                                    id: 'state',
                                    label: 'State',
                                    type: 'select',
                                    options: stateOptions,
                                    searchable: true,
                                    class: 'col-md-6',
                                    required: true,
                                    readonly: isApproved || isRestricted
                                },
                                {
                                    id: 'country',
                                    label: 'Country',
                                    type: 'select',
                                    options: countryOptions,
                                    class: 'col-md-6',
                                    required: true,
                                    readonly: isApproved || isRestricted
                                },
                                {
                                    id: 'addressType',
                                    label: 'Address Type',
                                    type: 'select',
                                    options: { 'Home': 'Home', 'Warehouse': 'Warehouse', 'Other': 'Other' },
                                    class: 'col-md-6',
                                    readonly: isApproved || isRestricted
                                }
                            ]}
                            fieldData={formData}
                            onChange={(field, value) => handleInputChange({ target: { name: field, value } })}
                            hTure={false}
                            authToken={authToken}
                        />
                    </div>

                    {/* Save Settings Button */}
                    <button type="submit" className="btn btn-primary" disabled={loading || isApproved}>
                        {loading ? 'Saving...' : selectedWarehouse ? 'Update Warehouse' : 'Create Warehouse'}
                    </button>
                </form>

                {/* Address Selection Modal */}
                <Modal show={showAddressModal} onHide={() => setShowAddressModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Select Address to Load</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {userAddresses.length === 0 ? (
                            <p>No addresses found. Please add an address first.</p>
                        ) : (
                            <div className="row">
                                {userAddresses.map((address) => (
                                    <div key={address.id} className="col-md-6 mb-3">
                                        <div className="card">
                                            <div className="card-body">
                                                <h6 className="card-title">{address.name}</h6>
                                                <p className="card-text small">
                                                    {address.address}<br />
                                                    {address.locality && <>{address.locality}<br /></>}
                                                    {address.city}, {address.state} - {address.pincode}<br />
                                                    {address.country}
                                                </p>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => handleLoadFromAddress(address)}
                                                >
                                                    Load This Address
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddressModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        )
    );
};

export default EditPickupLocation;
